/**
 * The Snus Outlet — volymtrappa i cart-drawern.
 *
 * Erbjuder varje korgrad nästa paketsteg. Stegen kommer ur temat, inte ur Rapi:
 * cart-drawer.liquid skriver radens steg som JSON i `data-tiers` (antal, procent,
 * etikett och radens summa på steget, räknad av snippets/tso-pack-price — samma
 * regel som produktkortet och produktsidan). Skriptet räknar aldrig ett pris.
 * Procenten ställs i temainställningen TSO — Mängdrabatt; Rapi drar rabatten.
 *
 * Fail-closed: saknas eller går stegen inte att läsa blir det INGEN remsa —
 * hellre tyst än ett löfte som rabattfunktionen inte infriar.
 *
 * Kvantiteten skrivs via temats egen `cart-items.updateQuantity()`, som äger
 * laddaren, /cart/change.js och omritningen. Ingen egen cart-kod här.
 */
if (!customElements.get('cart-tier-upsell')) {
  /**
   * Ett steg per vara. Har raden väl uppgraderats är den färdig — resten av
   * korgen får fortsätta erbjuda sina steg, men den här slutar tjata.
   *
   * Minnet ligger på modulnivå eftersom sektionen ritas om vid varje
   * korgändring och elementet därmed byggs nytt. sessionStorage bär det vidare
   * om kunden lämnar sidan och kommer tillbaka; går den inte att skriva till
   * (privat läge, full kvot) gäller minnet bara den här sidan.
   */
  const UPGRADED_STORE = 'tso:cart-tiers:upgraded';
  let upgraded = null;

  const takenSteps = () => {
    if (upgraded) return upgraded;
    try {
      const raw = sessionStorage.getItem(UPGRADED_STORE);
      upgraded = new Set(raw ? JSON.parse(raw) : []);
    }
    catch (error) {
      upgraded = new Set();
    }
    return upgraded;
  };

  const rememberStep = (key) => {
    takenSteps().add(key);
    try {
      sessionStorage.setItem(UPGRADED_STORE, JSON.stringify(Array.from(upgraded)));
    }
    catch (error) {
      // Ingen lagring att få: minnet lever vidare i modulen så länge sidan gör det.
    }
  };

  /**
   * Rader som lämnat korgen ska inte ligga kvar och tynga minnet.
   *
   * Tom lista rensar ingenting. Elementet ritas om flera gånger runt en
   * korgändring, och minst ett av varven träffar listan innan raderna hunnit
   * dit — rensade vi då skulle varan få tillbaka sina remsor direkt efter att
   * kunden uppgraderat den.
   */
  const forgetMissing = (keys) => {
    const live = new Set(keys.filter(Boolean));
    if (!live.size) return;

    let changed = false;

    for (const key of takenSteps()) {
      if (!live.has(key)) {
        upgraded.delete(key);
        changed = true;
      }
    }

    if (!changed) return;
    try {
      sessionStorage.setItem(UPGRADED_STORE, JSON.stringify(Array.from(upgraded)));
    }
    catch (error) {
      // Se rememberStep: utan lagring lever minnet bara i modulen.
    }
  };

  customElements.define(
    'cart-tier-upsell',
    class CartTierUpsell extends HTMLElement {
      // Belöningens tidslinje, samma som designen: bläcket ute (420) + texten
      // på plats (220 fördröjt + 320), stilla, sedan uttoning före omritningen.
      static SETTLE = 540;
      static HOLD = 700;
      static FADE = 200;

      // Fler än så och remsorna tar över raden.
      static MAX_RUNGS = 2;

      // Hur länge vi väntar in varulistan efter en omritning innan vi ger upp.
      static READY_TIMEOUT = 5000;
      static READY_POLL = 120;

      connectedCallback() {
        this.strings = this.readStrings();
        this.pending = null;

        this.observeDrawer();
        this.renderWhenReady();
      }

      disconnectedCallback() {
        // Sektionen ritas om medan en belöning spelar: skriv kvantiteten nu,
        // annars tappas uppgraderingen.
        this.flush();
        clearTimeout(this.readyTimer);
        if (this.drawerObserver) this.drawerObserver.disconnect();
      }

      /**
       * Vid en omritning kan varulistan ännu inte ha nått dokumentet när
       * elementet kopplas in. Vi försöker om tills den finns.
       *
       * Medvetet setTimeout och inte requestAnimationFrame: rAF fyrar inte i
       * en bakgrundsflik, så draweren hade stått tom tills fliken togs fram.
       */
      renderWhenReady() {
        clearTimeout(this.readyTimer);

        const deadline = Date.now() + CartTierUpsell.READY_TIMEOUT;
        const attempt = () => {
          if (this.findList() || Date.now() > deadline) {
            this.render();
            return;
          }
          this.readyTimer = setTimeout(attempt, CartTierUpsell.READY_POLL);
        };

        attempt();
      }

      /** Varulistan remsorna ska skjutas in i. */
      findList() {
        const scope = this.closest('.drawer__panel, .cart') || this.parentElement;
        const cartItems = scope && scope.querySelector('cart-items');
        return (cartItems && cartItems.querySelector('.horizontal-products')) || null;
      }

      /**
       * Drawern öppnas: saknas remsorna ritar vi om. Drawern stängs: en
       * belöning som fortfarande väntar måste skrivas nu, annars tappas
       * uppgraderingen.
       */
      observeDrawer() {
        const drawer = this.closest('cart-drawer');
        if (!drawer) return;

        this.drawerObserver = new MutationObserver(() => {
          if (!drawer.hasAttribute('open')) {
            this.flush();
          }
          else if (!this.pending && !this.list?.querySelector('.tso-tier')) {
            this.renderWhenReady();
          }
        });
        this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
      }

      readStrings() {
        try {
          return JSON.parse(this.getAttribute('data-strings')) || {};
        }
        catch (error) {
          return {};
        }
      }

      // Platshållarna är [percent] och [count], samma konvention som temats
      // quickOrderListStrings — Liquid kan inte bära {{ }} genom en strängliteral.
      t(key, replacements) {
        let string = this.strings[key];
        if (typeof string !== 'string') return '';

        for (const token in replacements) {
          string = string.split('[' + token + ']').join(replacements[token]);
        }
        return string;
      }

      money(cents) {
        if (window.theme && theme.Currency && theme.Currency.formatMoney) {
          return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
        }
        return (cents / 100).toFixed(2);
      }

      /* ── Rendering ─────────────────────────────────────────────────────── */

      render() {
        this.list = this.findList();
        if (!this.list) return;

        this.list.querySelectorAll('.tso-tier').forEach((el) => el.remove());

        const lines = Array.from(this.list.querySelectorAll('.horizontal-product'));
        forgetMissing(lines.map((line) => line.dataset.variantId));

        // Panelen är temats frifraktsmätare och sköter sig själv i Liquid —
        // volymspråket hör hemma på remsorna, där varje steg har sitt eget
        // pris och sin egen knapp.
        for (const line of lines) {
          const offer = this.offerFor(line);
          if (!offer) continue;

          line.insertAdjacentElement('afterend', this.buildBlock(line, offer));
        }

        this.bindClicks();
      }

      /** Radens data + de närmaste stegen den faktiskt kan ta. */
      offerFor(line) {
        const qty = parseInt(line.dataset.quantity, 10);
        const stock = parseInt(line.dataset.inventory, 10);
        const unit = parseInt(line.dataset.price, 10);
        const key = line.dataset.key;

        if (!(qty > 0) || !(unit > 0) || !key) return null;

        // Minnet hänger på varianten, inte på radnyckeln: Shopify räknar om
        // nyckelns hash när kvantiteten ändras, så den vi mindes dör i samma
        // stund uppgraderingen landar. Varianten överlever.
        const variantId = line.dataset.variantId;
        if (!variantId || takenSteps().has(variantId)) return null;

        // Radens steg ur temat, med summan på varje steg färdigräknad i Liquid.
        let tiers = [];
        try {
          tiers = JSON.parse(line.dataset.tiers || '[]');
        }
        catch (error) {
          return null;
        }

        const rungs = (Array.isArray(tiers) ? tiers : [])
          .map((tier) => ({
            qty: parseInt(tier.qty, 10),
            pct: parseFloat(tier.pct),
            total: parseInt(tier.total, 10),
            label: String(tier.label || '').trim()
          }))
          .filter((rung) => rung.qty > 0 && rung.pct > 0 && rung.total > 0 && rung.label)
          .sort((a, b) => a.qty - b.qty);

        // Aldrig ett steg lagret inte täcker.
        const reachable = rungs.filter((rung) => rung.qty <= stock);
        const next = reachable
          .filter((rung) => rung.qty > qty)
          .slice(0, CartTierUpsell.MAX_RUNGS);

        if (!next.length) return null;

        return {
          key: key,
          variantId: variantId,
          qty: qty,
          unit: unit,
          rungs: next
        };
      }

      /**
       * Vilken fraktbelöning ger steget? Samma regel och samma data som fraktrutan i panelen
       * (snippets/tso-shipping-info), så remsan och rutan säger alltid samma sak:
       * - data-free-from: fri frakt (0 = ingen).
       * - data-steps: rabatt på frakten som "5000:20,7000:30", från:procent.
       * - data-counted-total: korgen till jfr-pris (före rabatter), utan short date
       *   (snippets/tso-ship-eligible).
       * - data-uncounted: short date-radernas nycklar.
       * - data-has-regular: ordern har en vanlig vara.
       * Short date räknas inte (Lowe 12 okt 2026), så en uppgraderad short date-rad når inget nytt
       * steg. Rutan räknar jfr-priset (Lowe 12 okt 2026 kväll: priset före rabatter, som kassans
       * fraktrabatt), så varje extra dosa lägger till radens ordinarie styckpris (data-price), inte
       * stegets rabatterade pris.
       * Svar: 0 = inget nytt steg, 1–99 = procent rabatt på frakten, 100 = fri frakt.
       * Ingen ruta = inga fraktlöften. Utanför länderna vi skickar till (snippets/tso-ship-away,
       * <html data-tso-away>) heller inga.
       */
      shippingReward(offer, rung) {
        if (document.documentElement.hasAttribute('data-tso-away')) return 0;
        const box = this.querySelector('[data-tso-ship-info]');
        if (!box || !box.hasAttribute('data-has-regular')) return 0;
        if ((box.dataset.uncounted || '').split(',').includes(offer.key)) return 0;

        const counted = parseInt(box.dataset.countedTotal, 10);
        if (isNaN(counted)) return 0;

        const free = parseInt(box.dataset.freeFrom, 10) || 0;
        const steps = (box.dataset.steps || '').split(',')
          .map((step) => step.split(':').map(Number))
          .filter(([at, pct]) => at > 0 && pct > 0);
        const rewardAt = (total) => {
          if (free > 0 && total >= free) return 100;
          return steps.reduce((best, [at, pct]) => (total >= at && pct > best ? pct : best), 0);
        };

        const reward = rewardAt(counted + offer.unit * (rung.qty - offer.qty));
        return reward > rewardAt(counted) ? reward : 0;
      }

      // *…* i texten blir fetstil, som beloppen och belöningen i fraktrutan. Texten läggs som
      // textnoder, aldrig som HTML.
      rich(key, replacements) {
        const fragment = document.createDocumentFragment();
        this.t(key, replacements).split('*').forEach((part, index) => {
          if (!part) return;
          if (index % 2) {
            const strong = document.createElement('strong');
            strong.textContent = part;
            fragment.append(strong);
          }
          else {
            fragment.append(document.createTextNode(part));
          }
        });
        return fragment;
      }

      plain(key, replacements) {
        return this.t(key, replacements).split('*').join('');
      }

      buildBlock(line, offer) {
        const block = document.createElement('li');
        block.className = 'tso-tier';
        block.setAttribute('data-fresh', '');

        const caption = document.createElement('p');
        caption.className = 'tso-tier__caption';
        // Ikonen är vår egen markup; etiketten kommer ur locale-filen och
        // läggs som textnod, aldrig som HTML.
        caption.innerHTML = this.icon('percent');
        caption.append(document.createTextNode(this.t('caption')));
        block.append(caption);

        for (const rung of offer.rungs) {
          block.append(this.buildRow(offer, rung));
        }

        // Introanimationens fill vinner annars över svepets uttoning av
        // syskonstegen.
        setTimeout(() => block.removeAttribute('data-fresh'), 460);

        return block;
      }

      buildRow(offer, rung) {
        const add = rung.qty - offer.qty;
        const ship = this.shippingReward(offer, rung);
        const percent = String(rung.pct).replace(/\.0+$/, '');
        // Samma ord som fraktrutan: "+ free shipping" eller "+ 30% off shipping".
        const kind = ship >= 100 ? '_shipping' : (ship > 0 ? '_ship_off' : '');
        const replacements = { percent: percent, ship: ship };

        const row = document.createElement('div');
        row.className = 'tso-tier__row';
        row.dataset.key = offer.key;
        row.dataset.variant = offer.variantId;
        row.dataset.to = rung.qty;
        row.dataset.reward = this.plain('saved' + kind, replacements);

        const badge = document.createElement('span');
        badge.className = 'badge badge--onsale tso-tier__add-count';
        badge.textContent = '+' + add;

        const label = document.createElement('span');
        label.className = 'tso-tier__label';
        label.textContent = rung.label;

        const meta = document.createElement('span');
        meta.className = 'tso-tier__meta';
        // Två rader, så paketnamnet får plats på mobil: "Save 12%" och under den fraktbelöningen.
        const save = document.createElement('span');
        save.className = 'tso-tier__save';
        save.append(this.rich('save', replacements));
        meta.append(save);
        if (ship) {
          const shipLine = document.createElement('span');
          shipLine.className = 'tso-tier__ship';
          shipLine.append(this.rich(ship >= 100 ? 'ship_free' : 'ship_off', replacements));
          meta.append(shipLine);
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'tso-tier__add';
        button.setAttribute('aria-label', this.t('add_more', { count: add }));
        button.innerHTML = this.icon('cart');

        const burst = document.createElement('span');
        burst.className = 'tso-tier__burst';
        burst.setAttribute('aria-hidden', 'true');
        burst.innerHTML = '<span class="tso-tier__ink"></span><span class="tso-tier__saved">'
          + this.icon('check') + '<span></span></span>';
        burst.querySelector('.tso-tier__saved span').textContent = row.dataset.reward;

        row.append(badge, label, meta, button, burst);
        return row;
      }

      icon(name) {
        // Rabattrosetten framför rubriken. Designen bar den som en base64-PNG
        // använd som mask; här är samma form ritad som SVG, så den ärver
        // textfärgen och inte 3 kB bilddata.
        if (name === 'percent') {
          return '<svg class="tso-tier__cap-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round" aria-hidden="true">'
            + '<path d="M10 3.5Q12.75 1.53 13.82 4.74Q17.2 4.77 16.18 7.99Q18.91 10 16.18 12.01Q17.2 15.23 13.82 15.26'
            + 'Q12.75 18.47 10 16.5Q7.25 18.47 6.18 15.26Q2.8 15.23 3.82 12.01Q1.1 10 3.82 7.99Q2.8 4.77 6.18 4.74Q7.25 1.53 10 3.5Z"/>'
            + '<path d="M12.5 7.5 7.5 12.5" stroke-linecap="round"/>'
            + '<circle cx="8" cy="8" r="1.05"/><circle cx="12" cy="12" r="1.05"/></svg>';
        }
        if (name === 'check') {
          return '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true">'
            + '<path stroke-linecap="round" stroke-linejoin="round" d="m4.6 10.6 3.5 3.5 7.3-8.3"/></svg>';
        }
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">'
          + '<path stroke-linecap="round" stroke-linejoin="round" d="M1 1h.5v0c.226 0 .339 0 .44.007a3 3 0 0 1 2.62 1.976c.034.095.065.204.127.42l.17.597m0 0 1.817 6.358c.475 1.664.713 2.496 1.198 3.114a4 4 0 0 0 1.633 1.231c.727.297 1.592.297 3.322.297h2.285c1.75 0 2.626 0 3.359-.302a4 4 0 0 0 1.64-1.253c.484-.627.715-1.472 1.175-3.161l.06-.221c.563-2.061.844-3.092.605-3.906a3 3 0 0 0-1.308-1.713C19.92 4 18.853 4 16.716 4H4.857ZM12 20a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm8 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z"/></svg>';
      }

      /* ── Belöningen ────────────────────────────────────────────────────── */

      bindClicks() {
        if (this.list.dataset.tierBound) return;
        this.list.dataset.tierBound = 'true';
        this.list.addEventListener('click', this.onClick.bind(this));
      }

      onClick(event) {
        const button = event.target.closest('.tso-tier__add');
        if (!button) return;

        const row = button.closest('.tso-tier__row');
        if (!row) return;

        // Ett svep i taget: två parallella belöningar skulle skriva korgen två
        // gånger, och den andra remsan är ändå borta efter omritningen.
        if (this.pending) return;

        row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        this.sweep(row);

        const block = row.closest('.tso-tier');
        const quantity = parseInt(row.dataset.to, 10);

        setTimeout(() => block && block.setAttribute('data-burst-out', ''), CartTierUpsell.SETTLE + CartTierUpsell.HOLD);

        this.schedule(row.dataset.key, row.dataset.variant, quantity);
      }

      /**
       * Kvantiteten skrivs först när belöningen har synts — omritningen river
       * annars svepet mitt i. Stängs drawern eller ritas sektionen om innan
       * dess skrivs den direkt i stället, så uppgraderingen aldrig tappas.
       */
      schedule(key, variantId, quantity) {
        const run = () => {
          // Varianten minns steget; radnyckeln skriver det. Nyckeln byts av
          // Shopify vid kvantitetsändringen och duger inte som minne.
          rememberStep(variantId);
          const cartItems = this.list && this.list.closest('cart-items');
          if (cartItems) cartItems.updateQuantity(key, quantity);
        };

        this.pending = {
          run: run,
          timer: setTimeout(() => {
            this.pending = null;
            run();
          }, CartTierUpsell.SETTLE + CartTierUpsell.HOLD + CartTierUpsell.FADE)
        };
      }

      flush() {
        if (!this.pending) return;

        const pending = this.pending;
        this.pending = null;
        clearTimeout(pending.timer);
        pending.run();
      }

      /** Plusknappens fyllning växer ut ur knappen över hela remsan. */
      sweep(row) {
        const button = row.querySelector('.tso-tier__add');
        const box = row.getBoundingClientRect();
        const hit = button.getBoundingClientRect();
        const x = hit.left - box.left + hit.width / 2;
        const y = hit.top - box.top + hit.height / 2;

        // Cirkeln måste nå remsans bortersta hörn, annars står ett hörn ljust.
        const reach = Math.max(
          Math.hypot(x, y),
          Math.hypot(box.width - x, y),
          Math.hypot(x, box.height - y),
          Math.hypot(box.width - x, box.height - y)
        );

        row.style.setProperty('--burst-x', x + 'px');
        row.style.setProperty('--burst-y', y + 'px');
        row.style.setProperty('--burst-scale', Math.ceil(reach / (hit.width / 2)));

        const block = row.closest('.tso-tier');
        if (block) block.setAttribute('data-burst-parent', '');
        row.setAttribute('data-burst', '');
      }
    }
  );
}

